import { useEffect, useState } from 'react';
import axiosInstance from '../utility/axiosInstance';

export type StorageEstimateResource = 'volume' | 'snapshot' | 'backup';

export interface StorageEstimate {
    resource_type: StorageEstimateResource;
    size_gb: number;
    unit_hourly_rate: number;
    hourly_total: number;
    monthly_total: number;
    currency: 'GBP';
    pricing_available: true;
}

type StorageEstimateState =
    | { status: 'idle' | 'loading' | 'unavailable'; estimate: null }
    | { status: 'ready'; estimate: StorageEstimate };

/**
 * Fetches the server-side storage rate rather than inferring one from existing resources. This is
 * the exact per-GB rule the billing worker uses when it records volumes, snapshots and backups.
 */
export default function useStorageEstimate(resourceType: StorageEstimateResource, sizeGb: number | null): StorageEstimateState {
    const [state, setState] = useState<StorageEstimateState>({ status: 'idle', estimate: null });

    useEffect(() => {
        if (!Number.isFinite(sizeGb) || !sizeGb || sizeGb <= 0) {
            setState({ status: 'idle', estimate: null });
            return;
        }

        let active = true;
        setState({ status: 'loading', estimate: null });
        axiosInstance.get('/v1/billing/estimate', {
            params: { resource_type: resourceType, size_gb: sizeGb },
        }).then((response) => {
            if (!active) return;
            const estimate = response.data as Partial<StorageEstimate>;
            if (estimate.pricing_available === true
                && Number.isFinite(estimate.unit_hourly_rate)
                && Number.isFinite(estimate.hourly_total)
                && Number.isFinite(estimate.monthly_total)) {
                setState({ status: 'ready', estimate: estimate as StorageEstimate });
                return;
            }
            setState({ status: 'unavailable', estimate: null });
        }).catch(() => {
            if (active) setState({ status: 'unavailable', estimate: null });
        });

        return () => { active = false; };
    }, [resourceType, sizeGb]);

    return state;
}
